import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, amount: 0.15 }}
    transition={{ duration: 0.7, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLine = ({ children, delay = 0, as: Tag = "span", className = "", inView = false }) => {
  const motionProps = inView
    ? { whileInView: { y: "0%" }, viewport: { once: true, amount: 0.6 } }
    : { animate: { y: "0%" } };
  return (
    <Tag className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        transition={{ duration: 0.9, delay, ease: EASE }}
        {...motionProps}
      >
        {children}
      </motion.span>
    </Tag>
  );
};

export const Eyebrow = ({ children, light = false, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span className={`h-px w-8 ${light ? "bg-gold-400" : "bg-gold-600"}`} />
    <span className={`text-[11px] font-mono uppercase tracking-[0.24em] font-semibold ${light ? "text-gold-300" : "text-gold-600"}`}>
      {children}
    </span>
  </div>
);

export const SectionHead = ({ eyebrow, title, lede, light = false, className = "" }) => (
  <div className={`max-w-3xl ${className}`}>
    <Reveal>
      <Eyebrow light={light}>{eyebrow}</Eyebrow>
    </Reveal>
    <MaskedLine as="h2" inView delay={0.08} className={`mt-4 font-serif font-semibold tracking-tight leading-tight text-2xl sm:text-3xl lg:text-4xl ${light ? "text-white" : "text-navy-900"}`}>
      {title}
    </MaskedLine>
    {lede ? (
      <Reveal delay={0.18}>
        <p className={`mt-4 text-base leading-relaxed ${light ? "text-slate-300" : "text-slate-600"}`}>
          {lede}
        </p>
      </Reveal>
    ) : null}
  </div>
);

export const CountUp = ({ to, decimals = 0, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} className={className}>
      {val.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
};
